import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogActions,
} from '@mui/material';

import {
  getEventRuntime,
  startEvent,
  endEvent,
} from '../../services/event-runtime.service';

type Runtime = {
  eventId: string;
  name: string;
  gameType: string;
  gamePeriod: number;
  maxGamePeriods: number;
  inPlay: boolean;
  periodStatus?: 'ready' | 'running' | 'ended';
  timer: {
    startedAt: number | null;
    accumulatedMs: number;
    running: boolean;
  };
  permissions: {
    canControlTimer: boolean;
    canViewTimer: boolean;
  };
  log: { ts: number; action: string; note?: string }[];
};

export default function EventServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [confirm, setConfirm] = useState<'start' | 'end' | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');

  const loadRuntime = useCallback(async (silent = false) => {
    if (!id) return;

    try {
      if (!silent) {
        setLoading(true);
      }

      const data = await getEventRuntime(id);
      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load event runtime';

      setErrorText(String(message));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;

    loadRuntime();

    const interval = setInterval(() => {
      loadRuntime(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [id, loadRuntime]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!runtime) return;

      const { startedAt, accumulatedMs, running } = runtime.timer;

      let total = accumulatedMs;

      if (running && startedAt) {
        total += Date.now() - startedAt;
      }

      setDisplaySeconds(Math.floor(total / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [runtime]);

  useEffect(() => {
    if (!runtime) return;

    const { startedAt, accumulatedMs, running } = runtime.timer;

    let total = accumulatedMs;

    if (running && startedAt) {
      total += Date.now() - startedAt;
    }

    setDisplaySeconds(Math.floor(total / 1000));
  }, [runtime]);

  if (loading && !runtime) {
    return (
      <Box p={2}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!runtime) {
    return (
      <Box p={2}>
        <Typography color="error" sx={{ mb: 2 }}>
          {errorText || 'Event not found'}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  const minutes = Math.floor(displaySeconds / 60);
  const seconds = displaySeconds % 60;
  const isAdmin = runtime.permissions.canControlTimer;

  const phaseLabel =
    runtime.gamePeriod === 0 && !runtime.inPlay
      ? 'Pre-game'
      : runtime.gamePeriod >= runtime.maxGamePeriods && !runtime.inPlay
      ? 'Post-game'
      : 'During';

  return (
    <Box display="flex" height="100vh">
      <Box
        width="120px"
        bgcolor="#111"
        color="#fff"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        p={1}
      >
        {isAdmin && (
          <>
            <Button
              variant="contained"
              color="success"
              sx={{ mb: 1 }}
              onClick={() => setConfirm('start')}
            >
              Start
            </Button>

            <Button
              variant="contained"
              color="error"
              sx={{ mb: 1 }}
              onClick={() => setConfirm('end')}
            >
              End
            </Button>
          </>
        )}

        <Button
          variant="outlined"
          sx={{ mt: 'auto', color: '#fff', borderColor: '#fff' }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Box>

      <Box flex={1} p={2} overflow="auto">
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">{runtime.name}</Typography>
          <Typography variant="body2">{runtime.gameType}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {phaseLabel}
          </Typography>
          {!!errorText && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              {errorText}
            </Typography>
          )}
        </Paper>

        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h2">
            {minutes}:{seconds.toString().padStart(2, '0')}
          </Typography>

          <Typography variant="subtitle1">
            Period {runtime.gamePeriod} / {runtime.maxGamePeriods}
          </Typography>

          <Typography variant="body2">
            {runtime.inPlay ? 'In Play' : 'Stopped'}
          </Typography>

          <Typography variant="caption" display="block" sx={{ mt: 2 }}>
            Refreshing every 5 seconds
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Recent Actions
          </Typography>

          {runtime.log.length === 0 ? (
            <Typography variant="body2">No actions yet</Typography>
          ) : (
            runtime.log
              .slice(-5)
              .reverse()
              .map((l, i) => (
                <Typography key={i} variant="body2">
                  {new Date(l.ts).toLocaleTimeString()} — {l.action}
                </Typography>
              ))
          )}
        </Paper>
      </Box>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>
          {confirm === 'start' ? 'Start period?' : 'End period?'}
        </DialogTitle>

        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>

          <Button
            onClick={async () => {
              if (!id) return;

              try {
                if (confirm === 'start') {
                  const data = await startEvent(id);
                  setRuntime(data as Runtime);
                } else {
                  const data = await endEvent(id);
                  setRuntime(data as Runtime);
                }

                setErrorText('');
              } catch (error: any) {
                const message =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Action failed';

                setErrorText(String(message));
              } finally {
                setConfirm(null);
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}